import React, { useState, useMemo, lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import NotificationProvider from './contexts/NotificationContext';
import ErrorBoundary from './components/ErrorBoundary';
import { API_ENDPOINTS } from './utils/apiConfig';
import { useNotifications } from './contexts/NotificationContext';
import { usePermissions } from './hooks/usePermissions';
import { useTracking } from './hooks/useTracking';
import { TRACKING_EVENTS } from './utils/trackingEvents';
import { startVersionCheck, stopVersionCheck, getCurrentVersion } from './utils/versionCheck';
import { isDebugEnabled } from './utils/fetchInterceptor';
import logger from './utils/logger';
import packageInfo from '../package.json';

// Layout
import { MainLayout } from './components/Layout';
import { ProtectedRoute, AccessDenied } from './components/Auth';
import { CollaborationProvider } from './components/CollaborationProvider';

// Pages publiques (chargées immédiatement car accessibles sans auth)
import StatusPage from './components/StatusPage';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import SetupPassword from './components/SetupPassword';
import VerifyEmail from './components/VerifyEmail';

// Dashboard chargé immédiatement (page d'accueil après login)
import { DashboardContainer } from './components/Dashboard';

// =============================================================================
// LAZY LOADING - Composants chargés à la demande pour réduire le bundle initial
// Gain estimé: ~150-200 KB sur le bundle initial
// =============================================================================

// Composants principaux (chargés à la navigation)
const UsersList = lazy(() => import('./components/UsersList'));
const GroupsList = lazy(() => import('./components/GroupsList'));
const ClientsList = lazy(() => import('./components/ClientsList'));
const LocationsList = lazy(() => import('./components/LocationsList'));
const PlanningCalendar = lazy(() => import('./components/PlanningCalendar'));
const MyPlanningView = lazy(() => import('./components/MyPlanning'));
const HRPlanningView = lazy(() => import('./components/HRPlanningView'));
const RolesPermissions = lazy(() => import('./components/RolesPermissions'));
const HolidaysList = lazy(() => import('./components/HolidaysList'));
const EmailTemplatesList = lazy(() => import('./components/EmailTemplatesList'));
const UserImport = lazy(() => import('./components/UserImport'));
const GroupMembersManager = lazy(() => import('./components/GroupMembersManager'));
const QuickInterimForm = lazy(() => import('./components/QuickInterimForm'));

// Modules complexes (imports nommés via wrapper)
const WorksheetValidationList = lazy(() =>
  import('./components/DailyWorksheet').then(m => ({ default: m.WorksheetValidationList }))
);
const WorkSheetImportList = lazy(() =>
  import('./components/WorkSheetImport').then(m => ({ default: m.WorkSheetImportList }))
);
const AnalyticsDashboard = lazy(() =>
  import('./components/Analytics').then(m => ({ default: m.AnalyticsDashboard }))
);
const LeaveRequestsMain = lazy(() =>
  import('./components/LeaveRequests').then(m => ({ default: m.LeaveRequestsMain }))
);
const LeaveRequestsList = lazy(() =>
  import('./components/LeaveRequests').then(m => ({ default: m.LeaveRequestsList }))
);
const LeaveRequestsValidation = lazy(() =>
  import('./components/LeaveRequests').then(m => ({ default: m.LeaveRequestsValidation }))
);
const FeedbackMain = lazy(() =>
  import('./components/Feedback').then(m => ({ default: m.FeedbackMain }))
);
const ExpensesMain = lazy(() =>
  import('./components/Expenses').then(m => ({ default: m.ExpensesMain }))
);
const ExpensesList = lazy(() =>
  import('./components/Expenses').then(m => ({ default: m.ExpensesList }))
);
const ExpenseValidation = lazy(() =>
  import('./components/Expenses').then(m => ({ default: m.ExpenseValidation }))
);
const ExpensesDashboard = lazy(() =>
  import('./components/Expenses').then(m => ({ default: m.ExpensesDashboard }))
);
const ExpensesManagement = lazy(() =>
  import('./components/Expenses').then(m => ({ default: m.ExpensesManagement }))
);

// Composants de développement/test (rarement utilisés)
const OCRTestPage = lazy(() => import('./pages/OCRTest'));
const DistanceApiComparison = lazy(() => import('./components/DistanceApiComparison'));

// Composant de chargement pour Suspense
const LoadingFallback = () => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '200px',
    color: '#666'
  }}>
    <div style={{ textAlign: 'center' }}>
      <div style={{
        width: '40px',
        height: '40px',
        border: '3px solid #f3f3f3',
        borderTop: '3px solid #667eea',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
        margin: '0 auto 10px'
      }} />
      <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
      Chargement...
    </div>
  </div>
);

/**
 * Page de connexion
 */
const LoginPage = ({ onLogin, loading, error }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    onLogin(email, password);
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      padding: '20px',
      position: 'relative'
    }}>
      <div style={{
        background: 'white',
        padding: 'clamp(30px, 5vw, 50px)',
        borderRadius: '20px',
        boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
        width: '100%',
        maxWidth: '400px',
        backdropFilter: 'blur(10px)'
      }}>
        <div style={{ textAlign: 'center', marginBottom: '30px' }}>
          <img
            src="/logo_connect_transparant.png"
            alt="R-Connect"
            style={{
              height: '80px',
              width: 'auto'
            }}
          />
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontWeight: '600',
              color: '#333'
            }}>
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={{
                width: '100%',
                padding: '14px 16px',
                border: '2px solid #e1e5e9',
                borderRadius: '10px',
                fontSize: '16px',
                fontFamily: 'inherit',
                transition: 'border-color 0.2s ease',
                outline: 'none',
                boxSizing: 'border-box'
              }}
              onFocus={(e) => e.target.style.borderColor = '#667eea'}
              onBlur={(e) => e.target.style.borderColor = '#e1e5e9'}
              required
            />
          </div>

          <div style={{ marginBottom: '25px' }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontWeight: '600',
              color: '#333'
            }}>
              Mot de passe
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={{
                width: '100%',
                padding: '14px 16px',
                border: '2px solid #e1e5e9',
                borderRadius: '10px',
                fontSize: '16px',
                fontFamily: 'inherit',
                transition: 'border-color 0.2s ease',
                outline: 'none',
                boxSizing: 'border-box'
              }}
              onFocus={(e) => e.target.style.borderColor = '#667eea'}
              onBlur={(e) => e.target.style.borderColor = '#e1e5e9'}
              required
            />
          </div>

          {error && (
            <div style={{
              background: 'linear-gradient(135deg, #f8d7da, #f1aeb5)',
              color: '#721c24',
              padding: '12px 16px',
              borderRadius: '10px',
              marginBottom: '20px',
              fontSize: '14px',
              border: '1px solid #f5c6cb'
            }}>
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              padding: '14px',
              background: loading
                ? 'linear-gradient(135deg, #6c757d, #5a6268)'
                : 'linear-gradient(135deg, #667eea, #764ba2)',
              color: 'white',
              border: 'none',
              borderRadius: '10px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: loading ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s ease',
              boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
            }}
          >
            {loading ? 'Connexion...' : 'Se connecter'}
          </button>

          <div style={{ textAlign: 'center', marginTop: '15px' }}>
            <a
              href="/forgot-password"
              style={{
                color: '#667eea',
                textDecoration: 'none',
                fontSize: '14px'
              }}
            >
              Mot de passe oublié ?
            </a>
          </div>
        </form>
      </div>

      <div style={{
        position: 'absolute',
        bottom: '20px',
        left: 0,
        right: 0,
        textAlign: 'center',
        fontSize: '12px',
        color: 'rgba(255,255,255,0.6)'
      }}>
        <span>© {new Date().getFullYear()} 6B Digital - Renfort Connect</span>
        <span style={{ margin: '0 8px' }}>|</span>
        <span>v{packageInfo.version}</span>
      </div>
    </div>
  );
};

/**
 * Contenu principal de l'application (après authentification)
 */
const AppContent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { showSuccess, showInfo, showError } = useNotifications();
  const { trackPageView, trackAction } = useTracking();

  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showGroupMembersManager, setShowGroupMembersManager] = useState(false);
  const [showQuickInterim, setShowQuickInterim] = useState(false);

  // Ref pour éviter les vérifications de token répétées
  const tokenVerifiedRef = React.useRef(false);

  // Hook de gestion des permissions
  const { canAccessModule, hasPermission, isAdmin, role } = usePermissions(user);

  const handleLogin = async (email, password) => {
    setLoading(true);
    setError('');

    try {
      showInfo('Connexion en cours...', {
        title: 'Authentification',
        duration: 2000
      });

      // Cache-buster: ajouter timestamp pour éviter tout cache navigateur
      const loginUrl = `${API_ENDPOINTS.AUTH_LOGIN}?_t=${Date.now()}`;
      const response = await fetch(loginUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache, no-store',
          'Pragma': 'no-cache'
        },
        body: JSON.stringify({ email, password }),
        cache: 'no-store'  // Force le navigateur à ne pas cacher
      });

      const data = await response.json();

      if (data.success) {
        // 🔧 DEBUG LOGIN (contrôlé par isDebugEnabled() dans fetchInterceptor.js)
        if (isDebugEnabled()) {
          const token = data.data.token;
          try {
            const base64 = token.split('.')[1];
            const payload = JSON.parse(new TextDecoder().decode(
              Uint8Array.from(atob(base64), c => c.charCodeAt(0))
            ));
            console.log('🔧 [DEBUG LOGIN] Token reçu du backend:', {
              iat: payload.iat,
              exp: payload.exp,
              iatDate: new Date(payload.iat * 1000).toISOString(),
              expDate: new Date(payload.exp * 1000).toISOString(),
              nowDate: new Date().toISOString(),
              secondsUntilExpiry: payload.exp - Math.floor(Date.now() / 1000)
            });
          } catch (e) {
            logger.error('🔧 [DEBUG LOGIN] Erreur décodage token:', e);
          }
        }

        const userData = {
          ...data.data.user,
          permissions: data.data.user.permissions || []
        };

        setUser(userData);
        setToken(data.data.token);
        setIsLoggedIn(true);
        localStorage.setItem('token', data.data.token);

        if (isDebugEnabled()) {
          console.log('🔧 [DEBUG LOGIN] Token stocké dans localStorage');
        }

        trackAction(TRACKING_EVENTS.LOGIN_SUCCESS, 'user', null, {
          userId: userData.id,
          role: userData.role
        });

        showSuccess('Connexion réussie !', {
          title: 'Bienvenue',
          message: `Bonjour ${userData.name || email}`,
          details: `Rôle : ${userData.role}`
        });

        navigate('/dashboard');
      } else {
        setError(data.error || 'Identifiants incorrects');
        showError('Échec de la connexion', {
          title: 'Authentification échouée',
          message: data.error || 'Identifiants incorrects'
        });
      }
    } catch (err) {
      const errorMessage = 'Erreur de connexion au serveur';
      setError(errorMessage);
      showError('Problème de connexion', {
        title: 'Erreur réseau',
        message: 'Impossible de contacter le serveur'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    trackAction(TRACKING_EVENTS.LOGOUT, 'user', null, { userId: user?.id });

    // Réinitialiser le ref de vérification pour la prochaine connexion
    tokenVerifiedRef.current = false;

    setIsLoggedIn(false);
    setUser(null);
    setToken(null);
    localStorage.removeItem('token');

    showInfo('Déconnexion réussie', {
      title: 'À bientôt',
      message: 'Vous avez été déconnecté avec succès',
      duration: 3000
    });

    navigate('/');
  };

  // Décoder le payload JWT avec support UTF-8
  const decodeJWTPayload = (token) => {
    const base64 = token.split('.')[1];
    const binaryString = atob(base64);
    // Convertir en Uint8Array pour gérer correctement l'UTF-8
    const bytes = Uint8Array.from(binaryString, c => c.charCodeAt(0));
    return JSON.parse(new TextDecoder().decode(bytes));
  };

  // Vérifier le token au chargement (une seule fois)
  React.useEffect(() => {
    // Éviter les vérifications répétées
    if (tokenVerifiedRef.current || isLoggedIn) {
      return;
    }

    if (token) {
      tokenVerifiedRef.current = true;

      try {
        const payload = decodeJWTPayload(token);

        // Vérifier l'expiration du token
        const now = Date.now() / 1000;
        if (payload.exp && payload.exp < now) {
          // Token expiré : nettoyage silencieux, pas de message d'erreur
          // L'utilisateur verra simplement la page de connexion
          console.info('Session expirée, redirection vers la connexion');
          localStorage.removeItem('token');
          setToken(null);
          // Pas de showError ici - c'est un comportement normal après inactivité
          return;
        }

        const userName = payload.first_name && payload.last_name
          ? `${payload.first_name} ${payload.last_name}`
          : payload.name || payload.userName || 'Utilisateur';

        const userData = {
          id: payload.userId || payload.id,
          name: userName,
          role: payload.role || 'user',
          email: payload.email,
          permissions: payload.permissions || []
        };
        setUser(userData);
        setIsLoggedIn(true);

        showSuccess('Session restaurée', {
          title: 'Reconnexion automatique',
          message: `Bonjour ${userData.name}`,
          details: `Rôle : ${userData.role}`,
          duration: 4000
        });
      } catch (error) {
        // Token malformé ou corrompu : nettoyage silencieux
        logger.warn('Token invalide, nettoyage:', error.message);
        localStorage.removeItem('token');
        setToken(null);
        // Pas de showError - l'utilisateur verra la page de login normalement
      }
    }
  }, [token, isLoggedIn, showSuccess]);

  // Écouter les événements d'expiration de session (401 depuis les appels API)
  React.useEffect(() => {
    const handleUnauthorized = (event) => {
      // 🔧 DEBUG (contrôlé par isDebugEnabled() dans fetchInterceptor.js)
      if (isDebugEnabled()) {
        console.warn('🔒 [DEBUG AUTH] Événement auth:unauthorized reçu!', {
          reason: event?.detail?.reason,
          url: event?.detail?.url,
          hasToken: !!localStorage.getItem('token'),
          timestamp: new Date().toISOString()
        });
      }
      // Réinitialiser le ref de vérification pour la prochaine connexion
      tokenVerifiedRef.current = false;
      setIsLoggedIn(false);
      setUser(null);
      setToken(null);
      // Le token a déjà été supprimé par handleUnauthorized dans apiConfig.js
      navigate('/');
    };

    // Écouter les refresh automatiques de token pour synchroniser le state React
    // et mettre à jour les permissions si elles ont changé
    const handleTokenRefreshed = (event) => {
      const { token: newToken } = event.detail;
      if (newToken) {
        setToken(newToken);

        // Décoder le nouveau token pour extraire les permissions à jour
        try {
          const base64 = newToken.split('.')[1];
          const binaryString = atob(base64);
          const bytes = Uint8Array.from(binaryString, c => c.charCodeAt(0));
          const payload = JSON.parse(new TextDecoder().decode(bytes));

          // Mettre à jour le user avec les nouvelles permissions
          setUser(prevUser => {
            if (!prevUser) return prevUser;

            const newPermissions = payload.permissions || [];
            const oldPermissions = prevUser.permissions || [];

            // Vérifier si les permissions ont changé
            const permissionsChanged =
              newPermissions.length !== oldPermissions.length ||
              newPermissions.some(p => !oldPermissions.includes(p)) ||
              oldPermissions.some(p => !newPermissions.includes(p));

            if (permissionsChanged) {
              console.log('🔄 Permissions mises à jour:', { old: oldPermissions, new: newPermissions });
              return {
                ...prevUser,
                role: payload.role || prevUser.role,
                permissions: newPermissions
              };
            }
            return prevUser;
          });
        } catch (error) {
          logger.warn('Erreur décodage token lors du refresh:', error.message);
        }

        if (process.env.NODE_ENV === 'development') {
          console.log('🔄 State React token synchronisé avec le nouveau token');
        }
      }
    };

    window.addEventListener('auth:unauthorized', handleUnauthorized);
    window.addEventListener('auth:token-refreshed', handleTokenRefreshed);
    return () => {
      window.removeEventListener('auth:unauthorized', handleUnauthorized);
      window.removeEventListener('auth:token-refreshed', handleTokenRefreshed);
    };
  }, [navigate]);

  // Tracking des changements de page
  React.useEffect(() => {
    if (isLoggedIn && location.pathname) {
      trackPageView(location.pathname, { pageName: location.pathname });
    }
  }, [location.pathname, isLoggedIn, trackPageView]);

  // Heartbeat périodique pour maintenir le token valide
  // CONDITIONNÉ à l'activité utilisateur et la visibilité de l'onglet
  // Évite de garder le token actif si l'utilisateur est absent (PC en veille, etc.)
  React.useEffect(() => {
    if (!isLoggedIn || !token) return;

    const HEARTBEAT_INTERVAL = 10 * 60 * 1000; // 10 minutes
    const INACTIVITY_THRESHOLD = 5 * 60 * 1000; // 5 minutes d'inactivité max

    // Tracker la dernière activité utilisateur
    let lastActivity = Date.now();
    const updateActivity = () => { lastActivity = Date.now(); };

    // Écouter les événements d'activité (passive pour performance)
    const activityEvents = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
    activityEvents.forEach(event => {
      document.addEventListener(event, updateActivity, { passive: true });
    });

    const heartbeat = async () => {
      // Ne pas faire de heartbeat si :
      // - Onglet non visible (PC en veille, onglet en arrière-plan)
      // - Utilisateur inactif depuis plus de 5 minutes
      const isVisible = document.visibilityState === 'visible';
      const isActive = Date.now() - lastActivity < INACTIVITY_THRESHOLD;

      if (!isVisible || !isActive) {
        console.debug('Heartbeat ignoré (inactif ou onglet masqué)');
        return;
      }

      try {
        // Requête légère authentifiée pour déclencher le refresh si nécessaire
        await fetch(API_ENDPOINTS.PRESTATION_STATUSES, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        // Le fetchInterceptor gérera automatiquement le X-New-Token
      } catch (err) {
        // Silently fail - si le token est expiré, le 401 sera géré ailleurs
        console.debug('Heartbeat failed:', err.message);
      }
    };

    // Lancer immédiatement au montage si connecté
    heartbeat();

    // Puis toutes les 10 minutes
    const intervalId = setInterval(heartbeat, HEARTBEAT_INTERVAL);

    return () => {
      clearInterval(intervalId);
      activityEvents.forEach(event => {
        document.removeEventListener(event, updateActivity);
      });
    };
  }, [isLoggedIn, token]);

  // Version check : vérifie périodiquement si une nouvelle version est disponible
  // Force le rechargement de la page pour obtenir le nouveau JS
  // IMPORTANT: Tourne TOUJOURS (même sur page login) pour forcer le refresh du JS caché
  React.useEffect(() => {
    // Démarrer le check avec notification avant reload
    startVersionCheck(
      (newVersion) => {
        // Callback appelé quand une nouvelle version est détectée
        console.info(`🔄 Nouvelle version ${newVersion} détectée, rechargement...`);

        // Notification si connecté, sinon reload direct
        if (isLoggedIn) {
          showInfo(`Nouvelle version ${newVersion} disponible. Rechargement automatique dans 3 secondes...`, {
            title: 'Mise à jour',
            duration: 3000
          });
          setTimeout(() => {
            window.location.reload(true);
          }, 3000);
        } else {
          // Sur page login: reload immédiat sans notification
          window.location.reload(true);
        }
      }
    );

    return () => {
      stopVersionCheck();
    };
  }, [isLoggedIn, showInfo]);

  // Page de login si non connecté
  if (!isLoggedIn || !user) {
    return <LoginPage onLogin={handleLogin} loading={loading} error={error} />;
  }

  // Routes protégées avec le nouveau layout
  return (
    <CollaborationProvider
      userId={user?.id?.toString() || '1'}
      userName={user?.name || 'Utilisateur'}
      userRole={user?.role || 'user'}
      token={token}
    >
      <Suspense fallback={<LoadingFallback />}>
      <Routes>
        <Route element={<MainLayout user={user} token={token} onLogout={handleLogout} />}>
          {/* Redirection racine vers dashboard */}
          <Route index element={<Navigate to="/dashboard" replace />} />

          {/* Dashboard */}
          <Route path="dashboard" element={
            <DashboardContainer
              user={user}
              token={token}
              onNavigate={(page) => navigate(`/${page}`)}
            />
          } />

          {/* Planning général (lecture seule pour CE, éditable pour planificateurs) */}
          <Route path="planning" element={
            <ProtectedRoute moduleId="planning" user={user}>
              <PlanningCalendar token={token} readOnly={hasPermission('planning.view_own') && !hasPermission('planning.edit') && !hasPermission('planning.create')} />
            </ProtectedRoute>
          } />

          {/* Mon planning personnel (Employés, CE) */}
          <Route path="my-planning" element={
            <ProtectedRoute moduleId="my-planning" user={user}>
              <MyPlanningView token={token} user={user} />
            </ProtectedRoute>
          } />

          {/* Utilisateurs */}
          <Route path="users" element={
            <ProtectedRoute moduleId="users" user={user}>
              <UsersList
                token={token}
                onNavigateToGroups={() => navigate('/groups')}
              />
            </ProtectedRoute>
          } />

          {/* Groupes */}
          <Route path="groups" element={
            <ProtectedRoute moduleId="groups" user={user}>
              <GroupsList
                token={token}
                onOpenMembersManager={() => setShowGroupMembersManager(true)}
                onNavigateToUsers={() => navigate('/users')}
              />
            </ProtectedRoute>
          } />

          {/* Clients */}
          <Route path="clients" element={
            <ProtectedRoute moduleId="clients" user={user}>
              <ClientsList
                token={token}
                onNavigateToLocations={() => navigate('/locations')}
              />
            </ProtectedRoute>
          } />

          {/* Lieux */}
          <Route path="locations" element={
            <ProtectedRoute moduleId="locations" user={user}>
              <LocationsList
                token={token}
                onNavigateToClients={() => navigate('/clients')}
              />
            </ProtectedRoute>
          } />

          {/* RH */}
          <Route path="hr" element={
            <ProtectedRoute moduleId="hr" user={user}>
              <HRPlanningView token={token} />
            </ProtectedRoute>
          } />

          {/* Feuilles de travail */}
          <Route path="worksheets" element={
            <ProtectedRoute moduleId="worksheets" user={user}>
              <WorksheetValidationList token={token} />
            </ProtectedRoute>
          } />

          {/* Fiches de chantier (PDF) */}
          <Route path="work-sheets" element={
            <ProtectedRoute moduleId="work-sheets" user={user}>
              <WorkSheetImportList token={token} />
            </ProtectedRoute>
          } />

          {/* Mes Congés */}
          <Route path="my-leaves" element={
            <LeaveRequestsList token={token} userId={user?.id} />
          } />

          {/* Validation Congés (RH) */}
          <Route path="leaves-validation" element={
            <ProtectedRoute moduleId="leaves-validation" user={user}>
              <LeaveRequestsValidation token={token} />
            </ProtectedRoute>
          } />

          {/* Mes Frais */}
          <Route path="my-expenses" element={
            <ExpensesList token={token} userId={user?.id} />
          } />

          {/* Gestion Frais - Validation + Statistiques (Planificateur, Comptabilité, Admin) */}
          <Route path="expenses-validation" element={
            <ProtectedRoute moduleId="expenses-validation" user={user}>
              <ExpensesManagement token={token} user={user} />
            </ProtectedRoute>
          } />

          {/* Routes legacy (redirection) */}
          <Route path="leave-requests" element={
            <LeaveRequestsList token={token} userId={user?.id} />
          } />
          <Route path="expenses" element={
            <ExpensesList token={token} userId={user?.id} />
          } />

          {/* Feedback */}
          <Route path="feedback" element={
            <ProtectedRoute moduleId="feedback" user={user}>
              <FeedbackMain token={token} user={user} />
            </ProtectedRoute>
          } />

          {/* Jours fériés */}
          <Route path="holidays" element={
            <ProtectedRoute moduleId="holidays" user={user}>
              <HolidaysList token={token} />
            </ProtectedRoute>
          } />

          {/* Permissions (Admin) */}
          <Route path="permissions" element={
            <ProtectedRoute moduleId="permissions" user={user}>
              <RolesPermissions token={token} />
            </ProtectedRoute>
          } />

          {/* Templates Email (Admin) */}
          <Route path="email-templates" element={
            <ProtectedRoute moduleId="email-templates" user={user}>
              <EmailTemplatesList token={token} />
            </ProtectedRoute>
          } />

          {/* Import Utilisateurs (Admin) */}
          <Route path="user-import" element={
            <ProtectedRoute moduleId="user-import" user={user}>
              <UserImport />
            </ProtectedRoute>
          } />

          {/* Analytics (Admin) */}
          <Route path="analytics" element={
            <ProtectedRoute moduleId="analytics" user={user}>
              <AnalyticsDashboard token={token} />
            </ProtectedRoute>
          } />

          {/* OCR Test (Admin) */}
          <Route path="ocr-test" element={
            <ProtectedRoute moduleId="ocr-test" user={user}>
              <OCRTestPage />
            </ProtectedRoute>
          } />

          {/* APIs Distance Comparison (Admin) */}
          <Route path="distance-api" element={
            <ProtectedRoute moduleId="distance-api" user={user}>
              <DistanceApiComparison />
            </ProtectedRoute>
          } />

          {/* Accès refusé */}
          <Route path="access-denied" element={<AccessDenied />} />

          {/* 404 - redirection vers dashboard */}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Route>
      </Routes>

      {/* Modales globales */}
      {showGroupMembersManager && (
        <GroupMembersManager
          token={token}
          onClose={() => setShowGroupMembersManager(false)}
        />
      )}

      {showQuickInterim && (
        <QuickInterimForm
          onClose={() => setShowQuickInterim(false)}
          onSuccess={(newUser) => {
            setShowQuickInterim(false);
            showSuccess(`Intérimaire ${newUser.first_name} ${newUser.last_name} créé avec succès`);
          }}
        />
      )}
      </Suspense>
    </CollaborationProvider>
  );
};

/**
 * Composant principal avec providers
 */
function App() {
  return (
    <ErrorBoundary>
      <NotificationProvider>
        <Routes>
          {/* Routes publiques (hors layout) */}
          <Route path="/status" element={<StatusPage />} />
          <Route path="/forgot-password" element={
            <ForgotPassword onBackToLogin={() => window.location.href = '/'} />
          } />
          <Route path="/reset-password" element={
            <ResetPasswordWrapper />
          } />
          <Route path="/setup-password" element={
            <SetupPasswordWrapper />
          } />
          <Route path="/verify-email" element={
            <VerifyEmailWrapper />
          } />

          {/* Routes protégées */}
          <Route path="/*" element={<AppContent />} />
        </Routes>
      </NotificationProvider>
    </ErrorBoundary>
  );
}

/**
 * Wrapper pour ResetPassword avec extraction du token
 */
const ResetPasswordWrapper = () => {
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const token = urlParams.get('token');

  return (
    <ResetPassword
      token={token}
      onBackToLogin={() => window.location.href = '/'}
      onSuccess={() => window.location.href = '/'}
    />
  );
};

/**
 * Wrapper pour SetupPassword avec extraction du token (activation nouveau compte)
 */
const SetupPasswordWrapper = () => {
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const token = urlParams.get('token');

  return (
    <SetupPassword
      token={token}
      onBackToLogin={() => window.location.href = '/'}
      onSuccess={() => window.location.href = '/'}
    />
  );
};

/**
 * Wrapper pour VerifyEmail avec extraction du token (changement d'email)
 */
const VerifyEmailWrapper = () => {
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const token = urlParams.get('token');

  return (
    <VerifyEmail
      token={token}
      onBackToLogin={() => window.location.href = '/'}
      onSuccess={() => window.location.href = '/'}
    />
  );
};

export default App;
